/* eslint-disable react-compiler/react-compiler */
import {
  Block,
  BlockChange,
  Comment,
  RootComment,
  isRootComment,
} from '@basehub/data/replicache/index'
import {
  CheckCircledIcon,
  ChevronDownIcon,
  EnterIcon,
} from '@radix-ui/react-icons'
import { clsx } from 'clsx'
import { BaseHubAvatar } from '~/components/basehub-avatar'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { useUserData } from '~/hooks-data/use-user-data'
import { SimpleTooltip } from '~/sotano-kit/primitives/tooltip'
import { useTimeDiff } from '~/hooks/use-time-diff'
import {
  CommentForm,
  CommentFormFooter,
  CommentReactions,
  CommentThreadBox,
  CommentWrapper,
} from '../../../comments/components'
import {
  useBranchOrCommitSegment,
  useSelectedBranch,
} from '~/lib/commits/use-selected-branch-or-commit'
import { useUserStore } from '~/app/_store/user-store'
import { FeedbackCommentEditor } from '../../../comments/components/editor'
import { IconButton } from '~/sotano-kit/primitives/icon-button'
import * as React from 'react'
import { useToggleState } from '~/hooks/use-toggle-state'
import { CheckCircledFilledIcon } from '~/components/icons/check-circled-filled'
import { Skeleton } from '~/sotano-kit/primitives/skeleton'
import {
  PreviewBlockSwitch,
  isValidLayoutBlock,
} from '../../../explore/components/blocks/preview-block-switch'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import {
  BlockLabel,
  BlockWrapperStoreContext,
  createBlockWrapperStore,
} from '../../../explore/components/blocks/block-wrapper'
import { DiffChildBlock } from '../../../../components/diff/block/child'
import { DiffLayout } from '../../../../components/diff/diff-layout'
import { BlockIcon } from '~/components/block-icon'
import { getBlockTitleForDisplay } from '~/lib/get-block-title-for-display'
import { useBranchMergeDiff } from '../../_hooks/use-merge-diff'
import { Editor } from '@tiptap/core'
import { TreeManager } from '@basehub/utils/tree/index'
import { useDashboardSlugs } from '~/hooks/use-dashboard-slugs'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { Button, ButtonLink } from '~/sotano-kit/primitives/button'
import {
  MessageContext,
  addCommentOrReply,
  useCommentContent,
  useThread,
} from '../../../comments'
import { formatToSpecific } from '~/lib/time/utils'
import { useIsGuestInOthersBranch } from '~/hooks-data/use-user-role'
import { BlockIconStoreProvider } from '~/components/block-icon/store'

/* -------------------------------------------------------------------------- */
/*                              Comments                                      */
/* -------------------------------------------------------------------------- */

export const CRThread = ({
  root,
}: {
  root: Extract<Comment, { type: 'root' }>
}) => {
  const repoReplicache = useRepoReplicache().data
  const replicache = repoReplicache?.replicache
  const repo = repoReplicache?.repo
  const branch = useSelectedBranch().branch
  const branchId = branch?.id
  const currentUser = useUserStore((s) => s.optimisticUser)
  const editorRef = React.useRef<Editor | null>(null)
  const { isOn: isExpanded, handleToggle: toggleExpanded } = useToggleState()
  const { teamSlug, repoSlug } = useDashboardSlugs()
  const [currentEditingComment, setCurrentEditingComment] = React.useState<
    string | null
  >(null)

  const getChildrenKeys = useWorkingTreeStore((s) => s.tree?.getChildrenKeys)
  const isResolved = root.status === 'resolved'
  const renderComments = isResolved ? isExpanded : true
  const thread = useThread(root)

  return (
    <BlockIconStoreProvider getChildrenKeys={getChildrenKeys}>
      <div className="bg-dark-base-dark flex flex-col gap-2 rounded-lg overflow-hidden border border-dark-border-solid">
        {isResolved && (
          <ResolvedThreadHead {...{ isExpanded, root, toggleExpanded }} />
        )}
        <div className={renderComments ? 'flex flex-col pb-4' : 'hidden'}>
          <CommentThreadBox threadId={root.id}>
            {thread?.map((comment) => {
              const isEditingComment = currentEditingComment === comment.id
              return (
                <CRThreadComment
                  key={comment.id + (comment.lastEditedAt ?? '')}
                  setCurrentEditingComment={setCurrentEditingComment}
                  rootComment={root}
                  isEditingComment={isEditingComment}
                  comment={comment}
                  isResolved={isResolved}
                  editorRef={editorRef}
                />
              )
            })}
          </CommentThreadBox>

          <CommentForm
            editorKey={`comment-form-${root.id}`}
            mode="single-line"
            withBorder
            placeholder={isResolved ? 'Reply to reopen thread...' : undefined}
            className="whitespace-pre-wrap max-w-full overflow-hidden"
            editable
            alwaysShowPlaceholder={true}
            onSubmit={async (value, id) => {
              console.log('value', value)
              if (
                !replicache ||
                !branchId ||
                !currentUser?.username ||
                !branch ||
                !teamSlug ||
                !repoSlug ||
                !repo
              ) {
                return
              }
              await addCommentOrReply({
                id,
                thread,
                branchId,
                value,
                replicache,
                userId: currentUser.id,
                parentCommentId: thread?.[thread.length - 1]!.id,
                authorUsername: currentUser.username,
                branchName: branch.name,
                repoSlug,
                teamSlug,
                isDefaultBranch: branch.isDefault,
                repoName: repo.title,
              })
            }}
          />
        </div>
      </div>
    </BlockIconStoreProvider>
  )
}

const CRThreadComment = ({
  rootComment,
  comment,
  isResolved,
  isEditingComment,
  setCurrentEditingComment,
  editorRef,
}: {
  rootComment: RootComment
  comment: Comment
  isResolved: boolean
  isEditingComment: boolean
  setCurrentEditingComment: (id: string | null) => void
  editorRef: React.RefObject<Editor | null>
}) => {
  const replicache = useRepoReplicache().data?.replicache
  const currentUser = useUserStore((s) => s.optimisticUser)
  const isGuestInOthersBranch = useIsGuestInOthersBranch()

  const { content, isStreaming } = useCommentContent({
    comment,
    enableStreaming: true,
  })

  const editorKey = `comment-${comment.id}/$${
    isEditingComment ? 'editing' : 'viewing'
  }-${comment.lastEditedAt}`

  return (
    <CommentWrapper
      avatarBorder
      extraActions={
        Boolean(
          isRootComment(comment) && !isGuestInOthersBranch && !isResolved
        ) && (
          <SimpleTooltip content="Mark as resolved">
            <IconButton
              className="hover:text-dark-bright"
              onClick={() => {
                if (!replicache || !currentUser) return
                replicache.mutate.resolveComment({
                  id: comment.id,
                  userId: currentUser.id,
                })
              }}
            >
              <CheckCircledIcon className="size-3.5" />
            </IconButton>
          </SimpleTooltip>
        )
      }
      comment={comment}
      onEdit={
        currentUser?.id === comment.authorId
          ? () => {
              setCurrentEditingComment(comment.id)
              setTimeout(() => {
                editorRef.current?.commands.focus('end')
              }, 20)
            }
          : () => {}
      }
    >
      <div
        className={clsx(
          'flex flex-col gap-2 rounded-lg relative',
          isEditingComment && 'bg-dark-shade-hover p-2 cursor-text'
        )}
        onClick={(e) => {
          if (
            isEditingComment &&
            e.target instanceof HTMLElement &&
            // ignore buttons, inputs, labels and contenteditable elements
            ['BUTTON', 'INPUT', 'LABEL'].includes(e.target.nodeName) ===
              false &&
            !e.target.closest('[contenteditable]')
          ) {
            e.currentTarget
              .querySelector<HTMLElement>('[contenteditable]')
              ?.focus()
          }
        }}
      >
        <MessageContext.Provider value={{ comment, rootComment }}>
          {!!(content || !isStreaming) && (
            <FeedbackCommentEditor
              editorKey={editorKey}
              onCreate={({ editor }) => {
                editorRef.current = editor
              }}
              onUpdate={({ editor }) => {
                editorRef.current = editor
              }}
              onDestroy={() => {
                editorRef.current = null
              }}
              className="min-h-6"
              onSubmit={async (value) => {
                await replicache?.mutate.updateComment({
                  id: comment.id,
                  value,
                  lastEditedAt: new Date().toISOString(),
                })
                setCurrentEditingComment(null)
              }}
              key={comment.id}
              editable={isEditingComment}
              content={content}
              slotAfter={
                isEditingComment && (
                  <CommentFormFooter
                    handleSubmit={async (value) => {
                      await replicache?.mutate.updateComment({
                        id: comment.id,
                        value,
                        lastEditedAt: new Date().toISOString(),
                      })
                      setCurrentEditingComment(null)
                    }}
                    handleCancel={() => setCurrentEditingComment(null)}
                    commentIsEmpty={undefined}
                    editorKey={editorKey}
                  />
                )
              }
            />
          )}
          {isStreaming && (
            <div className="flex items-end gap-2 text-xs text-dark-faint mt-2">
              <span className="animate-pulse duration-1400">
                Streaming in...
              </span>
            </div>
          )}
        </MessageContext.Provider>
      </div>
      {isRootComment(comment) && comment.ref && comment.blockId && (
        <div className="pt-3">
          <RefPreview
            blockId={comment.blockId}
            type={comment.ref as 'diff' | 'block'}
          />
        </div>
      )}
      <CommentReactions isEditingComment={isEditingComment} comment={comment} />
    </CommentWrapper>
  )
}

const ResolvedThreadHead = ({
  isExpanded,
  root,
  toggleExpanded,
}: {
  isExpanded: boolean
  root: Extract<Comment, { type: 'root' }>
  toggleExpanded: () => void
}) => {
  const replicache = useRepoReplicache().data?.replicache
  const { data: closedBy } = useUserData(root.resolvedBy ?? root.authorId)
  const timeDiff = useTimeDiff(new Date(root.createdAt))
  const thread = useThread(root)
  const replyCount = (thread?.length ?? 1) - 1

  return (
    <div
      className={clsx('px-4 flex flex-col gap-2 py-4', isExpanded && 'pb-0')}
    >
      <div className="flex justify-between items-center">
        <div className="flex gap-2">
          <BaseHubAvatar
            withBorder
            id={root.resolvedBy ?? root.authorId}
            name={closedBy?.username ?? ''}
            size="small"
          />
          <div className="flex gap-1 items-center">
            <span className="text-sm text-dark-bright font-medium">
              {closedBy ? (
                closedBy.username
              ) : (
                <Skeleton width={60} height={14} />
              )}
            </span>
            <span className="text-sm text-dark-dim">resolved this thread</span>
            <SimpleTooltip content={formatToSpecific(new Date(root.createdAt))}>
              <span className="text-sm text-dark-faint">{timeDiff}</span>
            </SimpleTooltip>
          </div>
        </div>
        <div className="flex gap-1">
          <SimpleTooltip content={isExpanded ? 'Collapse' : 'Expand'}>
            <IconButton
              onClick={toggleExpanded}
              className="hover:text-dark-bright"
            >
              <ChevronDownIcon
                className={clsx(
                  'rotate-0 transform transition-transform',
                  isExpanded && 'rotate-180'
                )}
              />
            </IconButton>
          </SimpleTooltip>
          <SimpleTooltip content="Reopen thread">
            <IconButton
              onClick={() => {
                replicache?.mutate.openComment({
                  id: root.id,
                })
              }}
              className={clsx(
                'text-dark-accent !bg-dark-control-base/15 hover:!bg-dark-control-base/25'
              )}
            >
              <CheckCircledFilledIcon />
            </IconButton>
          </SimpleTooltip>
        </div>
      </div>
      {!isExpanded && (
        <Button
          unstyled
          focusStyled
          className="flex gap-3 px-8 cursor-default"
          onClick={toggleExpanded}
        >
          <span className="text-dark-faint text-xs hover:underline">
            {replyCount ? `${replyCount} Replies` : 'No replies'}
          </span>
        </Button>
      )}
    </div>
  )
}

const RefPreview = ({
  blockId,
  type,
}: {
  blockId: string
  type: 'diff' | 'block'
}) => {
  const { cr } = useSelectedBranch()

  switch (type) {
    case 'diff':
      if (!cr) return null
      return <DiffPreview blockId={blockId} crId={cr.id} />
    case 'block':
      return <BlockPreview blockId={blockId} />
    default:
      return null
  }
}

const DiffPreview = ({ crId, blockId }: { blockId: string; crId: string }) => {
  const { diffResult } = useBranchMergeDiff({ crId })

  const changesTree = diffResult?.diff?.changesTree
  const diffBlock = changesTree?.getNode(blockId)
  const parentDiffBlock = diffBlock?.parentKey
    ? changesTree?.getNode(diffBlock?.parentKey)
    : null

  if (!diffResult || !diffBlock || !diffResult.treeOld || !parentDiffBlock) {
    return <Skeleton height={32} />
  }
  return (
    <BlockPreviewWrapper
      blockId={blockId}
      getNode={changesTree?.getNode}
      type={'diff'}
    >
      <DiffLayout className="before:!absolute before:!h-full !my-0 group/diff-wrapper">
        <DiffChildBlock
          previewLink={
            diffBlock.value && (
              <LinkToBlockOrDiff
                block={parentDiffBlock.value}
                child={diffBlock.value}
                type="diff"
                className="hover:underline opacity-0 group-hover/diff-wrapper:opacity-100"
              />
            )
          }
          commentsControls={false}
          child={diffBlock.value}
          changesTree={diffResult.diff?.changesTree}
          treeNew={diffResult.treeNew}
          treeOld={diffResult.treeOld}
          index={0}
          statusFromParent={parentDiffBlock.value.status}
          displayBlockLink
        />
      </DiffLayout>
    </BlockPreviewWrapper>
  )
}

export const BlockPreviewWrapper = ({
  blockId,
  children,
  getNode,
  type,
}: {
  blockId: string
  getNode: TreeManager<BlockChange | Block>['getNode'] | undefined
  children: React.ReactNode
  type: 'block' | 'diff'
}) => {
  const node = getNode?.(blockId)

  if (
    node &&
    ['union', 'instance', 'component', 'document', 'list'].includes(
      node?.value.type ?? ''
    )
  ) {
    return (
      <div className="!mb-2 rounded-lg border border-dark-border-solid bg-dark-base overflow-hidden">
        <div className="flex group px-4 py-3 justify-between items-center group/preview-wrapper">
          <div className="flex gap-1.5 items-center">
            <BlockIcon block={node?.value} />
            <span className="text-sm text-dark-bright truncate">
              {getBlockTitleForDisplay(node?.value.title)}
            </span>
          </div>
          <LinkToBlockOrDiff
            className="group-hover/preview-wrapper:opacity-100 opacity-0 focus-within:opacity-100"
            block={node.value!}
            type={type}
          />
        </div>
      </div>
    )
  }

  const parent = node?.parentKey ? getNode?.(node?.parentKey) : null
  if (!parent) return null
  return (
    <div className="!mb-2 rounded-lg border border-dark-border-solid bg-dark-base overflow-hidden">
      <div className="flex group px-4 py-3 justify-between items-center group/preview-wrapper border-b border-dark-border-solid">
        <div className="flex gap-1.5 items-center">
          <BlockIcon block={parent?.value} />
          <span className="text-sm text-dark-bright truncate">
            {getBlockTitleForDisplay(parent?.value.title)}
          </span>
        </div>
        <LinkToBlockOrDiff
          child={node?.value}
          className="group-hover/preview-wrapper:opacity-100 opacity-0"
          block={parent.value!}
          type={type}
        />
      </div>
      <div className="relative overflow-hidden bg-dark-base p-4 pt-3">
        {children}
      </div>
    </div>
  )
}

const BlockPreview = ({ blockId }: { blockId: string }) => {
  const getNode = useWorkingTreeStore((s) => s.tree?.getNode)
  const node = getNode?.(blockId)
  const parentNode = node?.parentKey ? getNode?.(node?.parentKey) : null
  const path = useWorkingTreeStore((s) => s.tree?.getPath(blockId))

  const containerRef = React.useRef<HTMLDivElement>(null)
  const blockWrapperStore = React.useMemo(() => {
    return createBlockWrapperStore({
      scopeRef: containerRef,
      blockId: node?.value.id,
      isTouched: false,
    })
  }, [node?.value?.id])

  // parentNode is  InstanceBlock | ComponentBlock | DocumentBlock | UnionBlock
  return (
    <>
      <BlockWrapperStoreContext.Provider value={blockWrapperStore}>
        <BlockPreviewWrapper blockId={blockId} getNode={getNode} type="block">
          {node &&
          path &&
          parentNode &&
          isValidLayoutBlock(parentNode.value) ? (
            <div className="flex flex-col">
              {node.value.type !== 'comment' && (
                <BlockLabel
                  previewActions={
                    node.value && (
                      <LinkToBlockOrDiff
                        className="group-hover/block-preview-wrapper:opacity-100 opacity-0"
                        block={parentNode.value}
                        child={node?.value}
                        type="block"
                      />
                    )
                  }
                  parentBlock={parentNode.value}
                  block={node.value}
                  index={0}
                  path={path}
                  showActions={false}
                  className="!pb-1 group/block-preview-wrapper"
                />
              )}
              <PreviewBlockSwitch
                block={node.value}
                parent={parentNode.value}
                path={path}
              />
            </div>
          ) : null}
        </BlockPreviewWrapper>
      </BlockWrapperStoreContext.Provider>
    </>
  )
}

const LinkToBlockOrDiff = ({
  block,
  type,
  className,
  child,
  ...props
}: {
  block: Block | BlockChange
  type: 'block' | 'diff'
  child?: Block | BlockChange
} & React.HTMLAttributes<HTMLAnchorElement>) => {
  const { teamSlug, repoSlug } = useDashboardSlugs()
  const { encodedSegment } = useBranchOrCommitSegment()

  return (
    <ButtonLink
      unstyled
      href={`/${teamSlug}/${repoSlug}/explore/${encodedSegment}/${
        type === 'diff' ? 'diff:' : ''
      }${idToSourceBlockId(block)}${
        child ? `#${idToSourceBlockId(child)}` : ''
      }`}
      className={clsx(
        'flex items-center gap-0.5 outline-none text-dark-muted text-xs hover:text-dark-dim focus-within:text-dark-dim focus-within:opacity-100 hover:underline cursor-default focus-within:underline',
        className
      )}
      {...props}
    >
      <span>Open block</span>
      <EnterIcon className="size-3.5" />
    </ButtonLink>
  )
}
